import React from 'react';
import { Earth,Clock } from '../Icons/CustomIcons';
import { Advice } from '../Common/Card';
import DateTimeReportLine from './DateTimeReportLine';
import RecurringEventsDisplay from './RecurringEventsDisplay';
import { trackedLinkProps } from '../../services/analytics';

// «6. September 2026» – ausgeschriebenes Datum für die Fliesstexte.
const formatLongDate = (value) =>
  new Date(value).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });

// «Sa., 6. September 2026» – dasselbe mit vorangestelltem Wochentag.
const formatLongDateWithWeekday = (value) =>
  new Date(value).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAYS_EN_TO_JS = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6
};

// Normalize event_schedule.date_ranges (or a single from/until pair) into a list.
const resolveDateRanges = (ranges, fallbackFrom, fallbackUntil) => {
  if (Array.isArray(ranges) && ranges.length > 0) {
    return ranges.filter((r) => r && (r.from || r.until));
  }
  return (fallbackFrom || fallbackUntil) ? [{ from: fallbackFrom, until: fallbackUntil }] : [];
};

// True if dateObj (a Date at midnight) lies within at least one range.
// Returns true when no ranges are configured (no restriction).
const dateWithinRanges = (list, dateObj) => {
  if (!list || list.length === 0) return true;
  return list.some((r) => {
    const from = r.from ? new Date(r.from) : null; if (from) from.setHours(0, 0, 0, 0);
    const until = r.until ? new Date(r.until) : null; if (until) until.setHours(0, 0, 0, 0);
    return (!from || dateObj >= from) && (!until || dateObj <= until);
  });
};

// Renders one or more validity/accessibility date ranges as text.
const ValidityPeriods = ({ ranges, fallbackFrom, fallbackUntil, freelyAccessible }) => {
  const list = resolveDateRanges(ranges, fallbackFrom, fallbackUntil);
  if (list.length === 0) return null;
  const prefix = freelyAccessible ? 'Aktivität ist frei zugänglich' : 'Die Termine gelten';
  const fmt = (d) => new Date(d).toLocaleDateString('de-DE');
  const rangeText = (r) => {
    if (r.from && r.until) return `${fmt(r.from)} – ${fmt(r.until)}`;
    if (r.from) return `ab ${fmt(r.from)}`;
    return `bis ${fmt(r.until)}`;
  };
  if (list.length === 1) {
    return <p style={{ margin: 'var(--spacing-1) 0', color: '#6c757d' }}>{`${prefix} von ${rangeText(list[0])}.`}</p>;
  }
  return (
    <div style={{ margin: 'var(--spacing-1) 0', color: '#6c757d' }}>
      <p style={{ margin: 0 }}>{`${prefix} in folgenden Zeiträumen:`}</p>
      <ul style={{ margin: '2px 0 0', paddingLeft: '1.2em' }}>
        {list.map((r, i) => <li key={i}>{rangeText(r)}</li>)}
      </ul>
    </div>
  );
};

/**
 * Berechnet die nächsten Termine für wiederkehrende Events
 */
const getNextRecurringDates = (eventSchedule, filterDate) => {
  if (!eventSchedule) return [];
  
  const dates = [];
  const { recurrence, day_of_week, start_time, end_time, additional_times, valid_from, valid_until } = eventSchedule;
  const filterDateObj = new Date(filterDate);
  filterDateObj.setHours(0, 0, 0, 0); // Normalisiere auf Mitternacht
  
  // Datumsbereiche (mehrere möglich, z.B. Schulferien); Fallback auf einzelnes
  // valid_from/valid_until für Altdaten.
  const rangeList = resolveDateRanges(eventSchedule.date_ranges, valid_from, valid_until);

  // Anker für mehrwöchige Muster: explizites valid_from, sonst frühester
  // Bereichsbeginn.
  const startDate = valid_from ? new Date(valid_from) : null;
  if (startDate) startDate.setHours(0, 0, 0, 0);
  const anchorDate = startDate || (() => {
    const starts = rangeList
      .map((r) => r.from)
      .filter(Boolean)
      .map((s) => { const d = new Date(s); d.setHours(0, 0, 0, 0); return d.getTime(); });
    return starts.length ? new Date(Math.min(...starts)) : null;
  })();

  // Wenn recurrence nicht definiert ist, behandle es als 'daily'
  const effectiveRecurrence = recurrence || 'daily';

  // Tägliche Wiederholung
  if (effectiveRecurrence === 'daily') {
    // Prüfe ob filterDate im Gültigkeitsbereich liegt
    const isInRange = dateWithinRanges(rangeList, filterDateObj);
    if (isInRange) {
      dates.push({
        date: new Date(filterDateObj),
        start_time,
        end_time,
        additional_times: additional_times || []
      });
    }
    return dates;
  }
  
  // Wöchentliche/Zweiwöchentliche/Dreiwöchentliche/Vierwöchentliche Wiederholung
  if (day_of_week && (effectiveRecurrence === 'weekly' || effectiveRecurrence === 'biweekly' || 
                       effectiveRecurrence === 'triweekly' || effectiveRecurrence === 'fourweekly')) {
    const targetDay = DAYS_EN_TO_JS[day_of_week];
    const filterDay = filterDateObj.getDay();
    
    // Prüfe ob filterDate auf den richtigen Wochentag fällt
    if (filterDay === targetDay) {
      // Prüfe ob filterDate im Gültigkeitsbereich liegt
      const isInRange = dateWithinRanges(rangeList, filterDateObj);

      if (isInRange) {
        // Bei wöchentlich: immer hinzufügen
        if (effectiveRecurrence === 'weekly') {
          dates.push({
            date: new Date(filterDateObj),
            start_time,
            end_time,
            additional_times: additional_times || []
          });
        }
        // Bei mehrwöchentlich: prüfe ob es die richtige Woche ist
        else if (anchorDate) {
          const intervalWeeks = {
            'biweekly': 2,
            'triweekly': 3,
            'fourweekly': 4
          }[effectiveRecurrence];

          // Berechne Wochen-Differenz seit Anker
          const daysDiff = Math.floor((filterDateObj - anchorDate) / (1000 * 60 * 60 * 24));
          const weeksDiff = Math.floor(daysDiff / 7);
          
          if (weeksDiff % intervalWeeks === 0) {
            dates.push({
              date: new Date(filterDateObj),
              start_time,
              end_time,
              additional_times: additional_times || []
            });
          }
        }
      }
    }
    return dates;
  }
  
  // Monatliche Wiederholung
  if (day_of_week && effectiveRecurrence.startsWith('monthly_')) {
    const targetDay = DAYS_EN_TO_JS[day_of_week];
    const occurrence = effectiveRecurrence.split('_')[1]; // first, second, third, fourth, last
    
    // Berechne alle möglichen Termine für diesen Monat
    const monthDates = findMonthlyOccurrence(filterDateObj, targetDay, occurrence);
    
    // Prüfe ob filterDate mit einem der berechneten Daten übereinstimmt
    for (const monthDate of monthDates) {
      if (filterDateObj.getFullYear() === monthDate.getFullYear() &&
          filterDateObj.getMonth() === monthDate.getMonth() &&
          filterDateObj.getDate() === monthDate.getDate()) {
        // Prüfe ob im Gültigkeitsbereich
        const isInRange = dateWithinRanges(rangeList, monthDate);
        if (isInRange) {
          dates.push({
            date: new Date(monthDate),
            start_time,
            end_time,
            additional_times: additional_times || []
          });
          break; // Nur ein Termin pro filterDate
        }
      }
    }
    return dates;
  }
  
  return dates;
};

/**
 * Findet alle Vorkommen eines Wochentags in einem Monat oder den letzten
 * @returns Array von Date-Objekten
 */
const findMonthlyOccurrence = (date, targetDay, occurrence) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  
  if (occurrence === 'last') {
    // Letzter Wochentag des Monats
    const lastDay = new Date(year, month + 1, 0);
    const lastDayOfWeek = lastDay.getDay();
    const diff = (lastDayOfWeek - targetDay + 7) % 7;
    const result = new Date(year, month, lastDay.getDate() - diff);
    result.setHours(0, 0, 0, 0);
    return [result];
  }
  
  // Finde alle Vorkommen des Wochentags im Monat
  const dates = [];
  const firstDay = new Date(year, month, 1);
  const firstDayOfWeek = firstDay.getDay();
  const daysUntilTarget = (targetDay - firstDayOfWeek + 7) % 7;
  
  // Starte mit dem ersten Vorkommen
  let currentDate = 1 + daysUntilTarget;
  
  // Sammle alle Vorkommen (maximal 5)
  while (currentDate <= 31) {
    const testDate = new Date(year, month, currentDate);
    // Prüfe ob das Datum noch im gleichen Monat liegt
    if (testDate.getMonth() !== month) break;
    const resultDate = new Date(year, month, currentDate);
    resultDate.setHours(0, 0, 0, 0);
    dates.push(resultDate);
    currentDate += 7; // nächste Woche
  }
  
  return dates;
};

/**
 * Rendert alle Zeitslots für einen dateInfo-Eintrag.
 * activeTimeStart wird nicht zum Filtern genutzt – alle Zeiten werden immer angezeigt.
 */
const renderTimeSlots = (dateInfo) => {
  const allTimes = [];
  if (dateInfo.start_time) allTimes.push({ start_time: dateInfo.start_time, end_time: dateInfo.end_time });
  (dateInfo.additional_times || []).forEach(tp => allTimes.push(tp));

  if (allTimes.length === 0) return null;
  return allTimes.map((tp, i) => (
    <div key={i} className="ac-datetime-schedules">
      <Clock size={24} />
      <span className="body-m body--semibold line-height-default letter-spacing-tight">
        {tp.start_time}{tp.end_time && ` – ${tp.end_time}`} Uhr
      </span>
    </div>
  ));
};

/**
 * Gemeinsame Darstellungslogik für single_event und multi_day.
 * Gibt { upcomingDates, displayText } zurück.
 */
const buildEventDatesDisplay = (eventSchedule, showAll, filterDate) => {
  const eventDates = eventSchedule.dates || (eventSchedule.date ? [{
    date: eventSchedule.date, start_time: eventSchedule.start_time, end_time: eventSchedule.end_time
  }] : []);
  let dates = eventDates.map(ed => ({ date: new Date(ed.date), start_time: ed.start_time, end_time: ed.end_time, additional_times: ed.additional_times || [] }));

  if (showAll) {
    return {
      upcomingDates: dates,
      displayText: (
        <div className="opening-hours-list">
          {dates.map((dateInfo, index) => (
            <DateTimeReportLine
              key={index}
              label={<span className="nowrap">{formatLongDateWithWeekday(dateInfo.date)}</span>}
              dayData={dateInfo.start_time ? { opens: dateInfo.start_time, closes: dateInfo.end_time || '' } : null}
              onAdd={() => {}}
              onRemove={() => {}}
              onUpdate={() => {}}
            />
          ))}
        </div>
      ),
    };
  }

  const filterDateObj = new Date(filterDate); filterDateObj.setHours(0, 0, 0, 0);
  dates = dates.filter(dateInfo => {
    const d = new Date(dateInfo.date); d.setHours(0, 0, 0, 0);
    return d.getFullYear() === filterDateObj.getFullYear() && d.getMonth() === filterDateObj.getMonth() && d.getDate() === filterDateObj.getDate();
  });
  return { upcomingDates: dates, displayText: null };
};

// onWebsiteClick: optional, nur für Tracking (s. trackedLinkProps).
const EventScheduleDisplay = ({ eventType, openingHours, eventSchedule, accessibleFrom, accessibleUntil, website, filterDate, activeTimeStart, headerAction, onWebsiteClick }) => {
  let upcomingDates = [];
  let displayText = null;

  const renderWebsiteButton = () => website && (
    <div className="ac-website">
      <a
        href={website}
        target="_blank"
        rel="noopener noreferrer"
        className="activity-detail-action-btn activity-detail-action-btn--maps"
        {...trackedLinkProps(onWebsiteClick)}
      >
        <Earth size={26} color="var(--Colors-Accent-02)" stroke="var(--Colors-Black)" /> Mehr erfahren
      </a>
    </div>
  );

  const showAll = !filterDate; // Kontroll-Ansicht: kein Datum-Filter

  // Frei zugängliche Events
  if (eventType === 'freely_accessible') {
    const freelyRanges = resolveDateRanges(eventSchedule?.date_ranges, accessibleFrom, accessibleUntil);
    const showInfo = showAll || (() => {
      const filterDateObj = new Date(filterDate); filterDateObj.setHours(0, 0, 0, 0);
      return dateWithinRanges(freelyRanges, filterDateObj);
    })();

    if (showInfo) {
      displayText = (
        <div style={{ padding: 'var(--spacing-3)', backgroundColor: 'white', borderRadius: 'var(--radius-sm)' }}>
          <p style={{ fontWeight: '500', margin: 'var(--spacing-1) 0' }}>Frei zugänglich</p>
          <ValidityPeriods ranges={eventSchedule?.date_ranges} fallbackFrom={accessibleFrom} fallbackUntil={accessibleUntil} freelyAccessible />
        </div>
      );
    }
  }
  // Einzeltermin(e) / Mehrtägiger Event
  else if ((eventType === 'single_event' || eventType === 'multi_day') && eventSchedule) {
    ({ upcomingDates, displayText } = buildEventDatesDisplay(eventSchedule, showAll || eventType === 'multi_day', filterDate));
  }
  // Wiederkehrende Events
  else if (eventType === 'recurring_event' && eventSchedule) {
    if (showAll) {
      displayText = <RecurringEventsDisplay eventSchedule={eventSchedule} />;
    } else {
      upcomingDates = getNextRecurringDates(eventSchedule, filterDate);
    }
  }
  // Reguläre Öffnungszeiten
  else if (eventType === 'regular_hours' && openingHours) {
    if (showAll) {
      const DAYS_DE = { monday: 'Montag', tuesday: 'Dienstag', wednesday: 'Mittwoch', thursday: 'Donnerstag', friday: 'Freitag', saturday: 'Samstag', sunday: 'Sonntag' };
      displayText = (
        <>
          <div className="opening-hours-list">
            {DAYS_ORDER.filter(day => openingHours.some(h => h.day === day)).map(day => (
              <DateTimeReportLine
                key={day}
                label={DAYS_DE[day]}
                dayData={openingHours.find(h => h.day === day) || null}
                onAdd={() => {}}
                onRemove={() => {}}
                onUpdate={() => {}}
              />
            ))}
          </div>
          <ValidityPeriods
            ranges={eventSchedule?.date_ranges}
            fallbackFrom={eventSchedule?.valid_from || eventSchedule?.validFrom}
            fallbackUntil={eventSchedule?.valid_until || eventSchedule?.validUntil}
          />
        </>
      );
    } else {
      const filterDateObj = new Date(filterDate); filterDateObj.setHours(0,0,0,0);
      const jsDay = filterDateObj.getDay();
      const dayName = jsDay === 0 ? 'sunday' : DAYS_ORDER[jsDay - 1];
      const hours = openingHours.find(h => h.day === dayName);
      if (hours && hours.opens) {
        upcomingDates = [{ date: filterDateObj, start_time: hours.opens, end_time: hours.closes || null, additional_times: [] }];
      }
    }
  }

  return (
    <div className="event-schedule-display">
      {/* Header mit Titel und optionaler Beschreibung */}
      <div className="event-schedule-header-row">
        <div className="event-schedule-header">
          <h2 className="card-header">Termine</h2>
          <p className="card-subtitle">
            {filterDate ? (
              /* Aus dem Template-String in JSX-Fragmente aufgeteilt, damit das
                 Datum in einem eigenen <span class="nowrap"> stehen kann –
                 sonst bricht der Fliesstext mitten in «6. September 2026». */
              <>
                {eventType === 'multi_day'
                  ? 'Hier sehen Sie alle Termine der Aktivität, die am '
                  : 'Hier sehen Sie alle Termine vom '}
                <span className="nowrap">{formatLongDate(filterDate)}</span>
                {eventType === 'multi_day' ? ' beginnt.' : '.'}
              </>
            ) : eventType === 'multi_day'
                ? 'Hier sehen Sie alle Termine für diese Aktivität.'
                : 'Hier sehen Sie alle verfügbaren Termine.'}
          </p>
        </div>
        <div className="ac-website-desktop">{renderWebsiteButton()}</div>
      </div>

      {/* Wenn displayText gesetzt ist (z.B. für frei zugängliche Events oder Zusammenfassung von Wiederholungsregeln), zeige das anstelle der Terminliste */}
      {displayText || (upcomingDates.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {upcomingDates.map((dateInfo, index) => (
            <div key={index} className="ac-datetime-list">
              {showAll && (
                <p style={{ fontWeight: 500, margin: '0 0 var(--spacing-1)' }}>
                  <span className="nowrap">{formatLongDateWithWeekday(dateInfo.date)}</span>
                </p>
              )}
              {renderTimeSlots(dateInfo)}
            </div>
          ))}
        </div>
      ) : (
        <div className="ac-datetime-list">
          <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>Keine Termine verfügbar</p>
        </div>
      ))}

      <div className="ac-website-mobile">{renderWebsiteButton()}</div>

      <Advice text="Terminangaben und Texte ohne Gewähr. Bitte überprüfen Sie die Seite der Aktivität." />

      {headerAction && <div>{headerAction}</div>}
    </div>
  );
};

export default EventScheduleDisplay;
